import { useState, Dispatch, SetStateAction } from 'react'
import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { Form } from '@ant-design/compatible'
import TextArea from 'antd/lib/input/TextArea'
import { message } from 'antd'
import style from '@ProdWb/components/ExceptionFailureModal/styles.css'
import { useSelector } from 'react-redux'
import { isUserQCManager } from '@OpsWb/logic'

import {
    MutationOperations,
    removeExceptionNoteAttachment,
    handleExceptionAttachmentCreation,
} from '../logic'
import Text from '~/core/Text'
import Box from '~/core/Box'
import Link from '~/core/Link'
import UploadComponent from '~/components/FileUploadList/components/UploadComponent'
import { NoteAttachmentType } from '../types'
import { FORM_RULES, EXCEPTION_FORM_FIELDS } from '../constants'
import { ExceptionFailureQuery } from '~/gql/graphql'

type ExceptionFileUploadPropsType = FormComponentProps & {
    exception?: ExceptionFailureQuery['exceptionFailure']
    exceptionId?: string
    confirmFileUpload: (param: string) => void
    setExceptionAttachment: Dispatch<SetStateAction<NoteAttachmentType[]>>
    exceptionAttachment: NoteAttachmentType[]
}

const ExceptionFileUpload = (props: ExceptionFileUploadPropsType) => {
    const {
        exception,
        form: { getFieldDecorator },
        confirmFileUpload,
        setExceptionAttachment,
        exceptionAttachment,
    } = props

    const [exceptionFailureAttachments, setExceptionFailureAttachments] =
        useState<NoteAttachmentType[]>([])
    const isQCManager = useSelector(isUserQCManager)

    const filesMutationHandler = MutationOperations()

    const useOnSuccess = async (file: File) => {
        const fileAttachment =
            await filesMutationHandler.useMarkUploadAttachment(file)

        if (!fileAttachment) {
            message.error('Failed to upload attachment')
            return null
        }

        setExceptionAttachment(oldArray => [
            ...oldArray,
            {
                id: fileAttachment.attachmentId,
                url: fileAttachment.url,
                fileName: file.name,
            },
        ])

        message.success('Attachment uploaded successfully')
        confirmFileUpload('success')
        return fileAttachment
    }

    return (
        <>
            <Form.Item
                label={
                    <Text fontSize="16px" color="blackAlpha85">
                        Failure description
                        <Text color="blackAlpha45">
                            {' (required to escalate to failure)'}
                        </Text>
                    </Text>
                }
                style={{ marginBottom: '16px' }}
            >
                {getFieldDecorator(EXCEPTION_FORM_FIELDS.FAILURE_DESCRIPTION, {
                    initialValue: exception?.additionalContext || undefined,
                    rules: FORM_RULES.additionalContext,
                })(
                    <TextArea
                        placeholder={'Type failure description here'}
                        rows={3}
                        disabled={
                            !!(
                                exception?.correctiveActionRequestId &&
                                !isQCManager
                            )
                        }
                    />,
                )}
            </Form.Item>
            {!exception?.correctiveActionRequestId && (
                <Box direction="col" marginBottom="16px">
                    <Text fontSize="16px" color="blackAlpha85">
                        Failure attachments
                        <Text color="blackAlpha45">{' (optional)'}</Text>
                    </Text>
                    <Box display="flexInline" flexWrap="wrap">
                        {exceptionAttachment.map(attachment => (
                            <Box
                                direction="col"
                                key={attachment.id}
                                className={style.displayAttachmentStyle}
                            >
                                <Box>
                                    <Link
                                        data-test-target={`attachment_${attachment.id}_link`}
                                        href={attachment.url}
                                        target="_blank"
                                        className={style.attachmentLinkStyle}
                                    >
                                        <PaperClipOutlined />{' '}
                                        <Text>{attachment.fileName}</Text>
                                    </Link>
                                    <span
                                        onClick={() =>
                                            removeExceptionNoteAttachment(
                                                setExceptionFailureAttachments,
                                                exceptionFailureAttachments,
                                                filesMutationHandler,
                                                attachment.id,
                                            )
                                        }
                                    >
                                        <CloseOutlined
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </span>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Box marginTop="8px">
                        <UploadComponent
                            listType="text"
                            onCreate={file =>
                                handleExceptionAttachmentCreation(
                                    file,
                                    confirmFileUpload,
                                    filesMutationHandler,
                                )
                            }
                            onSuccess={useOnSuccess}
                            uploadTitle="Upload"
                            multiple
                            layoutType
                        />
                    </Box>
                </Box>
            )}
        </>
    )
}

export default ExceptionFileUpload
